import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";

import HeaderBread from "../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../DataLayer/DataLayer";
import Layout from "../../../../Layout";
import CustomHeaderTitle from "../../../../components/constants/CustomHeaderTitle";
import { getShorexBookingById } from "../../../../utils/ApiCalls/BackOffice/Shorex";

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toLowerCase()) {
    case "booked":
      return { ...base, background: "#0ab402" };
    case "failed":
      return { ...base, background: "#eb6363" };
    case "pending":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const fieldStyle = {
  display: "flex",
  gap: "8px",
  marginBottom: "10px",
  alignItems: "flex-start",
};

const labelStyle = {
  fontWeight: 600,
  minWidth: "160px",
  color: "#555",
  fontSize: "13px",
};

const valueStyle = {
  fontSize: "13px",
  color: "#222",
};

const formatJson = (value) => {
  if (value == null) return "-";
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(value);
  }
};

const ViewTransaction = () => {
  const { id } = useParams();
  const [, dispatch] = useDataLayerValue();
  const [transaction, setTransaction] = useState(null);

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Report", route: "/shore/back-office/shore-excursions/report" },
    { title: "View", route: "#" },
  ];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    const res = await getShorexBookingById(id);
    if (res.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: res.error, type: "error" },
      });
    } else {
      setTransaction(res.data || res);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  if (!transaction) return null;

  const guests = transaction.guests || [];

  const guestColumns = [
    { key: "resAccId", title: "Res Acc ID" },
    { key: "firstName", title: "First Name" },
    { key: "lastName", title: "Last Name" },
    { key: "type", title: "Type" },
    { key: "price", title: "Price" },
    { key: "cabinNumber", title: "Cabin Number" },
    { key: "dob", title: "Date of Birth" },
  ];

  return (
    <Layout type={"read_per"} module_data={"Report"}>
      <CustomHeaderTitle title={"View Shorex Booking Transaction"} />
      <div>
        <HeaderBread navigation={navigation} title={"View Transaction"} />
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">

            {/* Transaction Summary */}
            <div className="card" style={{ marginBottom: "20px" }}>
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Transaction Summary</h5>
              </div>
              <div className="card-body">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px 20px",
                  }}
                >
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Transaction ID:</span>
                    <span style={valueStyle}>{transaction.uniqId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Acc ID:</span>
                    <span style={valueStyle}>{transaction.accId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Exc ID:</span>
                    <span style={valueStyle}>{transaction.excId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Shorex Code:</span>
                    <span style={valueStyle}>{transaction.excNo || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Package Name:</span>
                    <span style={valueStyle}>{transaction.packageName || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Sailing ID:</span>
                    <span style={valueStyle}>{transaction.sailingId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Language:</span>
                    <span style={valueStyle}>{transaction.language || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Adult / Child / Infant Tickets:</span>
                    <span style={valueStyle}>
                      {transaction.adultTicket ?? 0} / {transaction.childTicket ?? 0} / {transaction.infantTicket ?? 0}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Total Price:</span>
                    <span style={valueStyle}>
                      {transaction.currency || ""}{" "}
                      {transaction.totalPrice != null
                        ? Number(transaction.totalPrice).toFixed(2)
                        : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Balance:</span>
                    <span style={valueStyle}>
                      {transaction.balance != null
                        ? Number(transaction.balance).toFixed(2)
                        : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Cruise ID:</span>
                    <span style={valueStyle}>{transaction.cruiseId || "-"}</span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Status:</span>
                    <span>
                      <span style={statusChipStyle(transaction.voided ? "voided" : transaction.status)}>
                        {(transaction.voided ? "voided" : transaction.status) || "-"}
                      </span>
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Created At:</span>
                    <span style={valueStyle}>
                      {transaction.createdAt
                        ? moment(transaction.createdAt).format("DD-MM-YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                  <div style={fieldStyle}>
                    <span style={labelStyle}>Updated At:</span>
                    <span style={valueStyle}>
                      {transaction.updatedAt
                        ? moment(transaction.updatedAt).format("DD-MM-YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Guests Table */}
            <div className="card" style={{ marginBottom: "20px" }}>
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Guests</h5>
              </div>
              <div className="card-body" style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    minWidth: "700px",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      {guestColumns.map((col) => (
                        <th
                          key={col.key}
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            borderBottom: "1px solid #e0e0e0",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {col.title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {guests.length === 0 ? (
                      <tr>
                        <td
                          colSpan={guestColumns.length}
                          style={{
                            padding: "16px 12px",
                            textAlign: "center",
                            color: "#999",
                          }}
                        >
                          No guests found
                        </td>
                      </tr>
                    ) : (
                      guests.map((guest, idx) => (
                        <tr
                          key={guest.id ?? idx}
                          style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                        >
                          {guestColumns.map((col) => (
                            <td
                              key={col.key}
                              style={{
                                padding: "8px 12px",
                                borderBottom: "1px solid #f0f0f0",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {guest[col.key] != null ? String(guest[col.key]) : "-"}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Request / Response */}
            <div className="card">
              <div className="card-header">
                <h5 style={{ margin: 0 }}>Fidelio Request / Response</h5>
              </div>
              <div className="card-body">
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ ...labelStyle, marginBottom: "6px" }}>Request</div>
                  <pre
                    style={{
                      background: "#f5f5f5",
                      padding: "10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      overflowX: "auto",
                      overflowY: "auto",
                      margin: 0,
                      maxHeight: "300px",
                    }}
                  >
                    {formatJson(transaction.request)}
                  </pre>
                </div>
                <div>
                  <div style={{ ...labelStyle, marginBottom: "6px" }}>Response</div>
                  <pre
                    style={{
                      background: "#f5f5f5",
                      padding: "10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      overflowX: "auto",
                      overflowY: "auto",
                      margin: 0,
                      maxHeight: "300px",
                    }}
                  >
                    {formatJson(transaction.response)}
                  </pre>
                </div>
              </div>
            </div>

          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ViewTransaction;
